import { useEffect, useState } from "react";
import Position from "../models/Position";
import WorkLogService from "../services/WorkLogService";
import PositionService from "../services/PositionService";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import { SUM_HOURS_WITH_MINIMUM_KEY } from "../types/BillingPolicy";
import { unwrap } from "../utils/queryFn";

interface UsePositionFormParams {
  externalId?: string;
  editMode: boolean;
  onClose: (action: "update" | "create" | "canceled") => void;
  projectExternalId?: string;
}

const usePositionForm = ({ externalId, editMode, onClose, projectExternalId }: UsePositionFormParams) => {
  const [isLoading, setIsLoading] = useState(false);
  const [instance, setInstance] = useState<Position | undefined>();
  const [showDialog, setShowDialog] = useState(true);
  const [hasWorklogs, setHasWorkLogs] = useState(false);
  const [newBillingPolicy, setNewBillingPolicy] = useState(false);
  const [shadowChecked, setShadowChecked] = useState(false);
  const [bonusValue, setBonusValue] = useState(0);

  useEffect(() => {
    if (editMode && externalId) {
      fetchPosition(externalId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPosition = async (externalId: string) => {
    setIsLoading(true);
    try {
      const [position, workLogs] = await Promise.all([
        unwrap(PositionService.getByExternalId(externalId)),
        unwrap(WorkLogService.findByPositionExternalId(externalId)),
      ]);

      setInstance(position);
      setHasWorkLogs(workLogs.length > 0);
      setNewBillingPolicy(position.billingPolicy?.toString() === SUM_HOURS_WITH_MINIMUM_KEY);
    } catch (error: any) {
      NotificationService.error("Error getting position");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeBillingPolicy = (event: any) => {
    const value = event.target.value;
    setNewBillingPolicy(value === SUM_HOURS_WITH_MINIMUM_KEY);
    setBonusValue(0);
  };

  const handleCheckShadow = () => {
    setShadowChecked((prev) => !prev);
    setBonusValue(0);
  };

  const handleChangeBonus = (event: any) => {
    const newValue = parseInt(event.target.value);
    setBonusValue(newValue);
    setShadowChecked(false);
  };

  const handleUpdatePosition = async (position: Position) => {
    try {
      await unwrap(PositionService.update(position));
      onClose("update");
    } catch (error: any) {
      NotificationService.error(error instanceof Error ? error.message : "Error updating position");
      onClose("canceled");
    }
  };

  const handleCreatePosition = async (position: Position) => {
    try {
      position.projectExternalId = projectExternalId;
      await unwrap(PositionService.create(position));
      onClose("create");
    } catch (error: any) {
      NotificationService.error(error instanceof Error ? error.message : "Error creating position");
      onClose("canceled");
    }
  };

  const handleOnCancel = () => {
    setShowDialog(false);
    setHasWorkLogs(false);
    setBonusValue(0);
    onClose("canceled");
  };

  return {
    isLoading,
    instance,
    showDialog,
    hasWorklogs,
    newBillingPolicy,
    shadowChecked,
    bonusValue,
    handleChangeBillingPolicy,
    handleCheckShadow,
    handleChangeBonus,
    handleUpdatePosition,
    handleCreatePosition,
    handleOnCancel,
  };
};

export default usePositionForm;
